// React hook for license management
// - Uses TanStack Query to fetch and cache license status
// - Provides mutations for purchasing and activating licenses
// - Handles loading and error states

import { type LicenseStatus, licenseService } from "../services/licenseService";
import { useMutation, useQuery, useQueryClient } from "./useDataCache";

// Module-level cache of the last successfully fetched license status.
// Mirrors the pattern in useHousehold — survives IDB restore windows and
// background re-fetches so the nav never disappears while data is in-flight.
let _lastKnownLicense: LicenseStatus | null = null;

export function clearLastKnownLicense() {
  _lastKnownLicense = null;
}

export function useLicense() {
  const queryClient = useQueryClient();

  // Fetch license status
  const {
    data: fetchedLicense,
    isLoading: queryIsLoading,
    isFetching,
    error,
    refetch,
  } = useQuery<LicenseStatus>({
    queryKey: ["license"],
    queryFn: async () => {
      const result = await licenseService.getLicenseStatus();
      if (result) _lastKnownLicense = result;
      return result;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes — licenses rarely change
    gcTime: 24 * 60 * 60 * 1000, // 24 hours — matches IDB persister maxAge
  });

  // Use last-known-good so the nav stays visible during background re-fetches
  // and the IDB restore window. Only truly "loading" when we have no data at all.
  const license = fetchedLicense ?? _lastKnownLicense ?? undefined;
  const isLoading = queryIsLoading && !license;

  // Mutation to purchase web license
  const purchaseWeb = useMutation({
    mutationFn: () => licenseService.purchaseWebLicense(),
    onSuccess: () => {
      // Invalidate license query after successful purchase redirect
      // (This won't execute because we redirect to Stripe, but good practice)
      queryClient.invalidateQueries({ queryKey: ["license"] });
    },
    onError: (error) => {
      console.error("🔴 Failed to initiate purchase:", error);
    },
  });

  // Mutation to activate app license
  const activateApp = useMutation({
    mutationFn: (platform: "google_play" | "app_store") =>
      licenseService.activateAppLicense(platform),
    onSuccess: () => {
      // Refetch license status after activation
      queryClient.invalidateQueries({ queryKey: ["license"] });
    },
    onError: (error) => {
      console.error("🔴 Failed to activate app license:", error);
    },
  });

  return {
    license,
    isLoading,
    isFetching,
    error,
    refetch,
    purchaseWeb,
    activateApp,
    canCreateHousehold: license?.canCreateHousehold ?? false,
    hasAnyLicense: license?.hasWebLicense || license?.hasAppLicense || false,
  };
}
